import React from 'react';
import type { ComponentFieldDefinition } from '@troia-v3/types';
import { FormField } from '@/shared/components/ui/FormField';
import { Select } from '@/shared/components/ui/Select';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PropertyReferenceSelector } from './PropertyReferenceSelector';

interface ReferenceSelectorProps {
  field: ComponentFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}

/**
 * Selector for reference fields. Fetches entity options from API
 * based on referenceConfig.entity.
 */
export const ReferenceSelector: React.FC<ReferenceSelectorProps> = ({
  field,
  value,
  onChange,
}) => {
  const ref = field.referenceConfig;
  if (!ref) return null;
  // Imóvel tem busca no servidor (a base pode ter milhares de itens).
  if (ref.entity === 'property') {
    return (
      <PropertyReferenceSelector
        field={field}
        value={typeof value === 'string' ? value : undefined}
        onChange={onChange}
      />
    );
  }

  const endpoint = getEndpointForEntity(ref.entity);
  const displayField = ref.displayField || 'name';

  const { data: options = [], isLoading } = useQuery({
    queryKey: ['reference', ref.entity, ref.filter],
    queryFn: async () => {
      const response = await api.get(endpoint, { params: { limit: 100 } });
      const items = response.data.data?.items || response.data.data || [];
      // Filtro por campo que a própria listagem já devolve (ex.: providerId do
      // canal). Evita rota nova só para estreitar a lista.
      const filtrados = ref.filter
        ? items.filter((item: Record<string, unknown>) =>
            Object.entries(ref.filter as Record<string, string>)
              .every(([campo, valor]) => String(item[campo] ?? '') === valor))
        : items;
      return filtrados.map((item: Record<string, unknown>) => ({
        label: String(item[displayField] || item.name || item.id),
        value: String(item.id),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  if (ref.multiple) {
    const selectedValues = (value as string[]) || [];

    return (
      <FormField label={field.label} description={field.description} required={field.required}>
        <div className="space-y-2">
          {/* Selected items */}
          {selectedValues.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedValues.map((v) => {
                const opt = options.find((o: { value: string }) => o.value === v);
                return (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs"
                    style={{
                      backgroundColor: 'var(--theme-badge-background)',
                      color: 'var(--theme-badge-text)',
                    }}
                  >
                    {opt?.label || v}
                    <button
                      type="button"
                      onClick={() => onChange(selectedValues.filter((sv) => sv !== v))}
                      className="hover:opacity-70"
                    >
                      &times;
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Add selector */}
          <Select
            value=""
            onChange={(val) => {
              if (val && !selectedValues.includes(val)) {
                onChange([...selectedValues, val]);
              }
            }}
            options={options.filter(
              (o: { value: string }) => !selectedValues.includes(o.value)
            )}
            placeholder={isLoading ? 'Carregando...' : `Selecione ${field.label}...`}
          />
        </div>
      </FormField>
    );
  }

  return (
    <FormField label={field.label} description={field.description} required={field.required}>
      <Select
        value={(value as string) || ''}
        onChange={(val) => onChange(val || undefined)}
        options={options}
        placeholder={isLoading ? 'Carregando...' : `Selecione ${field.label}...`}
      />
    </FormField>
  );
};

function getEndpointForEntity(entity: string): string {
  switch (entity) {
    case 'database':
      return 'databases';
    case 'funnelStep':
      return 'funnel-steps';
    case 'team':
      return 'teams';
    case 'user':
      return 'users';
    case 'channel':
      // dropdown: rota sem permissão de módulo (o editor de sites não exige
      // permissão de canais para listar um canal de widget)
      return 'channels/dropdown';
    default:
      return entity;
  }
}
